import * as React from "react";
import { IconChevronDown } from "../icons";

export type CollabVersionOption = {
  value: string;
  label: string;
};

export type CollabVersionSelectProps = {
  value: string;
  options: CollabVersionOption[];
  onChange: (value: string) => void;
  "aria-label"?: string;
  className?: string;
};

/** Compact outline dropdown — replaces antd Select for version filter. */
export const CollabVersionSelect: React.FC<CollabVersionSelectProps> = ({
  value,
  options,
  onChange,
  "aria-label": ariaLabel = "Filter by version",
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const label = options.find((o) => o.value === value)?.label ?? "All versions";

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      className={`collab-version-select${className ? ` ${className}` : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`collab-version-select__trigger${open ? " is-open" : ""}`}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{label}</span>
        <IconChevronDown size={12} />
      </button>
      {open ? (
        <ul className="collab-version-select__menu" role="listbox" aria-label={ariaLabel}>
          {options.map((opt, i) => {
            const selected = opt.value === value;
            return (
              <li key={opt.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`collab-version-select__option${selected ? " is-active" : ""}`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      const next = options[Math.min(i + 1, options.length - 1)];
                      if (next) onChange(next.value);
                    }
                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      const prev = options[Math.max(i - 1, 0)];
                      if (prev) onChange(prev.value);
                    }
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onChange(opt.value);
                      setOpen(false);
                    }
                  }}
                  autoFocus={i === activeIndex}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default CollabVersionSelect;
